import type {
  LifeEngine,
  LifeRegion,
  LifeSnapshot,
} from "../engine/engine";
import { parseRle } from "../patterns/rle";

export interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

export const DEFAULT_STEPS_PER_SECOND = 8;

function assertWorldPoint(point: WorldPoint): void {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
    throw new RangeError("Pattern center must contain finite coordinates.");
  }
}

function assertStepsPerSecond(value: number): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError("stepsPerSecond must be a positive finite number.");
  }
}

export class LifeSimulator {
  private runningState = false;
  private rate = DEFAULT_STEPS_PER_SECOND;
  private accumulatedMilliseconds = 0;

  constructor(private readonly engine: LifeEngine) {}

  get running(): boolean {
    return this.runningState;
  }

  get stepsPerSecond(): number {
    return this.rate;
  }

  setRunning(running: boolean): void {
    if (running === this.runningState) {
      return;
    }
    this.runningState = running;
    this.accumulatedMilliseconds = 0;
  }

  setStepsPerSecond(stepsPerSecond: number): void {
    assertStepsPerSecond(stepsPerSecond);
    this.rate = stepsPerSecond;
    this.accumulatedMilliseconds = 0;
  }

  step(): void {
    this.engine.step();
  }

  advance(generations: number): void {
    if (!Number.isSafeInteger(generations) || generations < 0) {
      throw new RangeError("generations must be a non-negative safe integer.");
    }

    for (let generation = 0; generation < generations; generation += 1) {
      this.engine.step();
    }
  }

  update(elapsedMilliseconds: number): number {
    if (!Number.isFinite(elapsedMilliseconds) || elapsedMilliseconds < 0) {
      throw new RangeError(
        "elapsedMilliseconds must be a non-negative finite number.",
      );
    }
    if (!this.runningState) {
      return 0;
    }

    this.accumulatedMilliseconds += elapsedMilliseconds;
    const stepInterval = 1000 / this.rate;
    const generations = Math.floor(
      this.accumulatedMilliseconds / stepInterval,
    );
    if (generations === 0) {
      return 0;
    }

    this.accumulatedMilliseconds -= generations * stepInterval;
    this.advance(generations);
    return generations;
  }

  clear(): void {
    this.engine.clear();
    this.runningState = false;
    this.accumulatedMilliseconds = 0;
  }

  loadPattern(rle: string, center: WorldPoint): void {
    assertWorldPoint(center);
    const pattern = parseRle(rle);
    const xOffset = Math.floor(center.x - pattern.width / 2);
    const yOffset = Math.floor(center.y - pattern.height / 2);
    const cells = pattern.cells.map(({ x, y }) => ({
      x: x + xOffset,
      y: y + yOffset,
    }));

    this.engine.replace(cells);
    this.runningState = false;
    this.accumulatedMilliseconds = 0;
  }

  getCell(x: number, y: number): boolean {
    return this.engine.getCell(x, y);
  }

  setCell(x: number, y: number, alive: boolean): void {
    this.engine.setCell(x, y, alive);
  }

  snapshot(region?: LifeRegion): LifeSnapshot {
    return this.engine.snapshot(region);
  }
}
